<template>
  <div class="Below MSRP-header-searchform position-relative" ref="root">
    <div class="woocommerce-product-search menu-search">

      <input
        ref="searchInput"
        type="text"
        class="search-field"
        name="search"
        autocomplete="off"
        role="combobox"
        aria-autocomplete="list"
        :aria-expanded="showList ? 'true' : 'false'"
        aria-controls="search-suggestions"
        :placeholder="placeholder"
        v-model="message"
        required
        @focus="focused = true"
        @keydown.down.prevent="move(1)"
        @keydown.up.prevent="move(-1)"
        @keydown.enter="onEnter"
        @keydown.esc="focused = false"
      />

      <button
        :type="buttonType"
        class="top-footer-search btn btn-primary d-inline-block mb-0"
        @click="$refs.searchInput.focus()"
      >
        <slot>
          <font-awesome-icon :icon="['fas', 'search']" />
        </slot>
      </button>
    </div>

    <!-- Live suggestions: the list narrows with every letter, so picking a
         model never needs the search button. Only shown when the page passes
         suggestions; pages that do not keep the old filter-as-you-type box. -->
    <ul
      v-if="showList"
      id="search-suggestions"
      class="search-suggestions"
      role="listbox"
    >
      <li
        v-for="(item, index) in matches"
        :key="item.label + index"
        role="option"
        class="search-suggestion"
        :class="{ 'is-active': index === activeIndex }"
        :aria-selected="index === activeIndex ? 'true' : 'false'"
        @mousedown.prevent="select(item)"
        @mouseenter="activeIndex = index"
      >
        <span class="search-suggestion__label">
          <template v-for="(part, pi) in highlight(item.label)" :key="pi">
            <mark v-if="part.hit">{{ part.text }}</mark><template v-else>{{ part.text }}</template>
          </template>
        </span>
        <span v-if="item.hint" class="search-suggestion__hint">{{ item.hint }}</span>
      </li>
      <li v-if="!matches.length" class="search-suggestion search-suggestion--empty">
        No models match "{{ message.trim() }}"
      </li>
    </ul>
  </div>
</template>

<script>
const MAX_SUGGESTIONS = 8;

export default {
  components: { },
  props: {
    placeholder: {
      type: String,
    },
    buttonType: {
      type: String,
    },
    // [{ label, hint?, ...anything }] — the whole item is emitted on select.
    suggestions: {
      type: Array,
      default() {
        return [];
      },
    },
  },
  emits: ["onTyping", "select"],
  data() {
    return {
      message: "",
      focused: false,
      activeIndex: 0,
    };
  },
  computed: {
    query() {
      return this.message.trim().toLowerCase();
    },
    // Names starting with the query come first, then names containing it.
    matches() {
      if (!this.query) return [];
      const starts = [];
      const contains = [];
      for (const item of this.suggestions) {
        const label = String(item?.label || "").toLowerCase();
        if (label.startsWith(this.query)) starts.push(item);
        else if (label.includes(this.query)) contains.push(item);
      }
      return [...starts, ...contains].slice(0, MAX_SUGGESTIONS);
    },
    showList() {
      return this.focused && this.query.length > 0 && this.suggestions.length > 0;
    },
  },
  watch: {
    message() {
      this.activeIndex = 0;
      this.focused = true;
      this.$emit("onTyping", this.message);
    },
  },
  mounted() {
    document.addEventListener("mousedown", this.onOutside);
  },
  beforeUnmount() {
    document.removeEventListener("mousedown", this.onOutside);
  },
  methods: {
    onOutside(e) {
      if (this.$refs.root && !this.$refs.root.contains(e.target)) this.focused = false;
    },
    move(step) {
      if (!this.showList || !this.matches.length) return;
      const n = this.matches.length;
      this.activeIndex = (this.activeIndex + step + n) % n;
    },
    onEnter(e) {
      if (!this.showList || !this.matches.length) return;
      e.preventDefault();
      this.select(this.matches[this.activeIndex] || this.matches[0]);
    },
    select(item) {
      this.focused = false;
      this.$emit("select", item);
    },
    highlight(label) {
      const text = String(label || "");
      const at = text.toLowerCase().indexOf(this.query);
      if (!this.query || at < 0) return [{ text, hit: false }];
      return [
        { text: text.slice(0, at), hit: false },
        { text: text.slice(at, at + this.query.length), hit: true },
        { text: text.slice(at + this.query.length), hit: false },
      ].filter((p) => p.text);
    },
  },
};
</script>

<style scoped="scoped">
.search-field {
  margin-left: 10px;
}
.top-footer-search {
  border-radius: 20px !important;
}
.menu-search {
  background: rgba(255, 255, 255, 0.07);
  border: 1px solid rgba(255, 255, 255, 0.2);
  box-shadow: none;
  border-radius: 30px;
  font-size: 12px;
  padding-left: 24px;
}
.menu-search:focus-within {
  border-color: #2563cc;
  background: rgba(255, 255, 255, 0.11);
}
.btn.btn-primary:hover {
  background: #3b82f6;
  border-color: #3b82f6;
}
.btn.btn-primary {
  background: #2563cc;
  padding: 0;
  border-radius: 50px !important;
  padding: 8px 13px;
  box-shadow: none;
  outline: 0;
  transition: all 250ms ease;
  position: absolute;
  right: 0;
  margin-right: -1px;
}

.btn {
  font-weight: 400;
  text-align: center;
  vertical-align: middle;
  cursor: pointer;
  background-image: none;
  border: 1px solid transparent;
  white-space: nowrap;
  padding: 6px 12px;
  font-size: 14px;
  line-height: 1.42857143;
  border-radius: 4px;
  outline: 0;
  height: 42px;
}
.menu-search .search-field {
  border: 0;
  outline: 0;
  background: transparent;
  line-height: 40px;
  color: #ffffff;
  font-size: 14px;
  font-weight: 300;
  width: calc(100% - 60px);
  box-sizing: border-box;
}

@media screen and (min-width: 750px) and (max-width: 980px) {
  .menu-search {
    padding: 0;
  }
}

.menu-search .search-field::placeholder {
  color: rgba(255, 255, 255, 0.5);
}

/* Solid panel so the page does not show through the list. */
.search-suggestions {
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  right: 0;
  z-index: 30;
  margin: 0;
  padding: 6px;
  list-style: none;
  background: #101d31;
  border: 1px solid rgba(255, 255, 255, 0.14);
  border-radius: 16px;
  box-shadow: 0 18px 44px rgba(0, 0, 0, 0.5);
  max-height: 320px;
  overflow-y: auto;
  overscroll-behavior: contain;
}
/* Chrome/Edge prefer the standard scrollbar-* properties over
   ::-webkit-scrollbar when both are set, and on Windows the standard one draws
   up/down arrow buttons. So the standard properties are for Firefox only. */
@supports not selector(::-webkit-scrollbar) {
  .search-suggestions {
    scrollbar-width: thin;
    scrollbar-color: rgba(255, 255, 255, 0.22) transparent;
  }
}
.search-suggestions::-webkit-scrollbar {
  width: 8px;
}
.search-suggestions::-webkit-scrollbar-button {
  display: none;
  width: 0;
  height: 0;
}
.search-suggestions::-webkit-scrollbar-track {
  background: transparent;
  margin: 8px 0;
}
.search-suggestions::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  border: 2px solid #101d31;
}
.search-suggestions::-webkit-scrollbar-thumb:hover {
  background: rgba(255, 255, 255, 0.35);
}
.search-suggestion {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 14px;
  border-radius: 10px;
  color: rgba(255, 255, 255, 0.88);
  font-size: 14px;
  cursor: pointer;
}
.search-suggestion.is-active {
  background: rgba(44, 115, 219, 0.22);
  color: #ffffff;
}
.search-suggestion mark {
  background: transparent;
  color: #7fb2f5;
  font-weight: 600;
  padding: 0;
}
.search-suggestion__hint {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.5);
  white-space: nowrap;
}
.search-suggestion--empty {
  cursor: default;
  color: rgba(255, 255, 255, 0.55);
}
</style>
